import dayjs from "dayjs";

import backgroundImages from "./background-images";
import { AccessCarouselData, AccessType } from "./types";
import { createLocalSongAssets } from "@utils/player/data/localAudioPaths";
import type { MobileDatabase } from "../../../../runtime/ports";
import type { Song } from "@utils/player/domain/types";
import type { ArtistAccessCarousel } from "@libs/wire";

export const resolveActiveSongForAccessCarousel = (
  activeSong: Song | null,
  db: MobileDatabase,
): Song | null => {
  if (!activeSong) return null;
  return (
    createLocalSongAssets(db).substituteLocalSongAssets([activeSong]).at(0) ??
    activeSong
  );
};

const createTopSongsCard = (artistId: string): AccessCarouselData => ({
  id: `top-${artistId}`,
  type: AccessType.TOP,
  image: backgroundImages.top,
  title: "Artist Profile",
  subtitle: "Check top open songs",
});

const createDjSetCard = (
  activeSong: Song | null,
): AccessCarouselData | null => {
  if (!activeSong?.isSetList || !activeSong.setList?.entries?.length) {
    return null;
  }

  return {
    id: `dj-set-${activeSong.id}`,
    type: AccessType.DJ_SET,
    image: backgroundImages[AccessType.DJ_SET],
    title: "DJ Set",
    subtitle: "Open full tracklist",
    ctaText: "Open",
    setListEntries: activeSong.setList.entries,
  };
};

export const buildAccessCarouselData = ({
  artistId,
  carouselData,
  isCarouselFetched,
  activeSong,
  unlockedSongIds,
}: {
  artistId: string;
  carouselData: ArtistAccessCarousel | undefined;
  isCarouselFetched: boolean;
  activeSong: Song | null;
  unlockedSongIds: string[] | undefined;
}): AccessCarouselData[] => {
  const topSongsCard = createTopSongsCard(artistId);
  const cards: AccessCarouselData[] = [];
  const djSetCard = createDjSetCard(activeSong);

  if (djSetCard) {
    cards.push(djSetCard);
  }

  if (isCarouselFetched && carouselData) {
    const musicItem = carouselData.music;
    const isUnlocked = musicItem
      ? unlockedSongIds?.includes(musicItem.songId)
      : false;

    if (musicItem && !isUnlocked) {
      cards.push({
        id: musicItem.accessId,
        type: AccessType.MUSIC,
        image: backgroundImages.music,
        title: musicItem.title,
        subtitle: musicItem.subtitle,
        unlocked: false,
        songImage: musicItem.artworkUrl ?? undefined,
        canPreview: musicItem.hasPreview,
      });
    }

    const eventItem = carouselData.event;

    if (eventItem) {
      // Venue-local `{ date, time }` resolved server-side (2026-07-23
      // convention): render literally. Parse the plain date locally (no shift);
      // append the 24h time to the weekday subtitle when present.
      const local = eventItem.startsAtLocal;
      const eventDate = dayjs(local.date);
      const month = eventDate.format("MMM");
      const day = eventDate.format("DD");
      cards.push({
        id: eventItem.accessId,
        type: AccessType.EVENT,
        image: backgroundImages.event,
        title: eventItem.title,
        subtitle: [
          local.time
            ? `${eventDate.format("ddd")} ${local.time}`
            : eventDate.format("ddd"),
          eventItem.city,
        ]
          .filter(Boolean)
          .join(" • "),
        startDateMonth: month,
        startDateDay: day,
        ctaText: "Details",
      });
    }
  }

  if (cards.length < 3) {
    cards.push(topSongsCard);
  }

  return cards;
};
